import { useEffect } from "react";
import type { PreventableEvent, WidgetActions } from "@/types";
import { useWidgetStore } from "@/state";
import {
  injectHtmlAndExecuteScripts,
  openChatAndReconnectIfNeeded,
  redirectTo,
  hasVendorChatDom,
  cleanupVendorChatDom,
  setReloginIntent,
  clearAlreadyLoggedInCache,
} from "@/utils";
import { postChatbotChat } from "@/api";
import { ensureChatbotHolder } from "@/core";
import { WidgetEvent$ } from "@/core/widgetBus";

const preventHard = (e?: PreventableEvent) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
  e?.stopImmediatePropagation?.();
};

const withState = <T>(
  fn: (st: ReturnType<typeof useWidgetStore.getState>["state"]) => T,
) => fn(useWidgetStore.getState().state);

export const useInitActions = () => {
  const setActions = useWidgetStore((s) => s.setActions);

  useEffect(() => {
    const actions: WidgetActions = {
      loginFn: (e) => {
        preventHard(e);
        WidgetEvent$.next({ type: "LOGIN_CLICK" });
        clearAlreadyLoggedInCache();
        withState((st) => {
          if (st.urls.loginUrl) redirectTo(st.urls.loginUrl, "top");
        });
      },
      reLoginFn: (e) => {
        preventHard(e);
        // clearLocalSession();
        // notifyHostLogout();
        WidgetEvent$.next({ type: "RELOGIN_CLICK" });

        withState((st) => {
          const loginUrl = st.urls.loginUrl;
          const logoutUrl = st.urls.logouturl;

          if (!logoutUrl || !loginUrl) return;
          setReloginIntent(st.clientId);
          clearAlreadyLoggedInCache();
          window.location.replace(logoutUrl);
        });
      },
      logoutFn: (e) => {
        preventHard(e);
        // clearLocalSession();
        // notifyHostLogout();
        WidgetEvent$.next({ type: "LOGOUT_CLICK" });
        withState((st) => {
          if (!st.urls.logouturl) return;
          clearAlreadyLoggedInCache();
          window.location.replace(st.urls.logouturl);
        });
      },
      gotoProfileFn: (e) => {
        preventHard(e);
        WidgetEvent$.next({ type: "PROFILE_CLICK" });
        withState((st) => {
          if (st.urls.profileUrl) redirectTo(st.urls.profileUrl, "self");
        });
      },
      openChatbotFn: (e) => {
        preventHard(e);
        const st = useWidgetStore.getState();
        st.setChatbotModalOpen(true);

        WidgetEvent$.next({ type: "CHAT_OPEN" });

        if (!st.chatbotAuthPayload) {
          st.setChatbotStatus("error", "Missing chatbot ticket payload");
        } else {
          st.setChatbotStatus("idle", null);
        }
      },
      continueChatbotFn: (e) => {
        preventHard(e);
        const st = useWidgetStore.getState();

        if (st.chatbotStatus === "loading") return;

        const payload = st.chatbotAuthPayload;
        if (!payload) {
          st.setChatbotStatus("error", "Missing chatbot ticket payload");
          return;
        }

        if (st.chatbotInjected && hasVendorChatDom()) {
          openChatAndReconnectIfNeeded();
          st.setChatbotStatus("ready", null);
          st.setChatbotModalOpen(false);
          return;
        }

        if (st.chatbotInjected && !hasVendorChatDom()) {
          st.setChatbotInjected(false);
        }

        st.setChatbotStatus("loading", null);

        void (async () => {
          try {
            const html = await postChatbotChat(
              payload as Record<string, unknown>,
            );

            const latest = useWidgetStore.getState();
            if (!latest.chatbotModalOpen) return;

            cleanupVendorChatDom();

            const holder = ensureChatbotHolder();
            injectHtmlAndExecuteScripts(holder, html);

            const after = useWidgetStore.getState();
            after.setChatbotInjected(true);
            after.setChatbotStatus("ready", null);
            after.setChatbotModalOpen(false);

            openChatAndReconnectIfNeeded();
            WidgetEvent$.next({ type: "CHAT_CONTINUE" });
          } catch (err) {
            console.error("[telekomId] chatbot chat failed:", err);
            const latest = useWidgetStore.getState();
            if (!latest.chatbotModalOpen) return;
            latest.setChatbotStatus("error", "Chatbot request failed");
          }
        })();
      },
      closeChatbotFn: (e) => {
        preventHard(e);
        const st = useWidgetStore.getState();
        WidgetEvent$.next({ type: "CHAT_CLOSE" });
        st.setChatbotModalOpen(false);
      },
    };

    setActions(actions);
  }, [setActions]);
};
